import { useCallback, useEffect, useRef, useState } from 'react';

interface SignatureModalProps {
  visible: boolean;
  orderNo: string;
  finalAmount: number;
  submitting: boolean;
  onClose: () => void;
  /** 确认签名，返回签名图 base64（PNG，白底） */
  onConfirm: (base64: string) => void;
}

/**
 * 电子签名弹窗（横屏版）：
 * - 竖屏手机：整个弹窗旋转90°铺满屏幕，签名区长边=屏幕高度（约3倍于原底部抽屉）
 * - 横屏/桌面：居中大弹窗，不旋转
 * - 画布用 clientWidth/clientHeight 取布局尺寸（不受 transform 影响）；
 *   旋转态指针坐标经 AABB 公式换算：rotate(90deg) 时 x=clientY-rect.top, y=rect.right-clientX
 */
export default function SignatureModal({ visible, orderNo, finalAmount, submitting, onClose, onConfirm }: SignatureModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef<{ x: number; y: number } | null>(null);
  const prevMidRef = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInk] = useState(false);
  const [rotated, setRotated] = useState(false); //竖屏时旋转90°呈现横屏
  const [modalSize, setModalSize] = useState({ w: 0, h: 0 });

  /** 初始化/重置画布：白底、线型、按 dpr 缩放 */
  const resetCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth || 300;
    const h = canvas.clientHeight || 150;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    drawingRef.current = false;
    lastPtRef.current = null;
    prevMidRef.current = null;
    setHasInk(false);
  }, []);

  //按窗口朝向决定弹窗尺寸（旋转态：宽=屏高、高=屏宽，旋转后正好铺满屏幕）
  useEffect(() => {
    const update = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (vh > vw) {
        setRotated(true);
        setModalSize({ w: vh, h: vw });
      } else {
        setRotated(false);
        setModalSize({ w: Math.min(720, Math.round(vw * 0.92)), h: Math.min(560, Math.round(vh * 0.9)) });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  //弹窗打开/尺寸变化时初始化画布（等 DOM 渲染后再量尺寸）
  useEffect(() => {
    if (visible) {
      setHasInk(false);
      requestAnimationFrame(resetCanvas);
    }
  }, [visible, modalSize.w, modalSize.h, resetCanvas]);

  /** 屏幕坐标 → 画布坐标（旋转态容器整体 rotate(90deg)） */
  const getPos = (e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rotated) {
      //顺时针旋转90°后：画布x沿屏幕向下、画布y沿屏幕向左
      return { x: e.clientY - rect.top, y: rect.right - e.clientX };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    setHasInk(true);
    lastPtRef.current = getPos(e);
    prevMidRef.current = null;
    //落笔画一个点，方便写"点、顿"
    const ctx = e.currentTarget.getContext('2d');
    if (ctx && lastPtRef.current) {
      ctx.beginPath();
      ctx.arc(lastPtRef.current.x, lastPtRef.current.y, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fillStyle = ctx.strokeStyle as string;
      ctx.fill();
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || !lastPtRef.current) return;
    e.preventDefault();
    const ctx = e.currentTarget.getContext('2d');
    if (!ctx) return;
    const pt = getPos(e);
    //中点二次贝塞尔平滑：以上一点为控制点、相邻中点为端点
    const mid = { x: (lastPtRef.current.x + pt.x) / 2, y: (lastPtRef.current.y + pt.y) / 2 };
    const prev = lastPtRef.current;
    ctx.beginPath();
    ctx.moveTo(prevMidRef.current?.x ?? prev.x, prevMidRef.current?.y ?? prev.y);
    ctx.quadraticCurveTo(prev.x, prev.y, mid.x, mid.y);
    ctx.stroke();
    prevMidRef.current = mid;
    lastPtRef.current = pt;
  };

  const handlePointerUp = () => {
    drawingRef.current = false;
    lastPtRef.current = null;
    prevMidRef.current = null;
  };

  const handleConfirm = () => {
    if (!hasInk || submitting) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    onConfirm(canvas.toDataURL('image/png'));
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 遮罩 */}
      <div className="absolute inset-0 bg-black/40" onClick={() => !submitting && onClose()} />
      {/* 弹窗主体：竖屏时旋转90°铺满屏幕（横屏视角），居中定位 */}
      <div
        className="absolute left-1/2 top-1/2 bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{
          width: modalSize.w ? `${modalSize.w}px` : undefined,
          height: modalSize.h ? `${modalSize.h}px` : undefined,
          transform: rotated
            ? 'translate(-50%, -50%) rotate(90deg)'
            : 'translate(-50%, -50%)',
        }}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-5 py-3 border-b border-background-100">
            <h3 className="text-foreground-900 text-base font-bold">电子签名确认付款</h3>
            <button
              onClick={() => !submitting && onClose()}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="关闭"
            >
              <i className="ri-close-line text-xl text-foreground-500" />
            </button>
          </div>

          <p className="text-foreground-400 text-xs px-5 py-2">
            订单 {orderNo} · 应付 <span className="text-primary-500 font-bold">¥{finalAmount.toLocaleString()}</span>
            ，请签名确认已向卖家付款
          </p>

          {/* 签名区：flex-1 铺满剩余空间 */}
          <div className="flex-1 relative px-4 min-h-0">
            <canvas
              ref={canvasRef}
              className="block w-full h-full rounded-xl border-2 border-dashed border-background-300 bg-white touch-none cursor-crosshair"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              onPointerCancel={handlePointerUp}
            />
            {!hasInk && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-background-300 text-sm">请在此区域内签名</span>
              </div>
            )}
          </div>

          <div className="flex gap-3 px-5 pt-3 pb-5">
            <button
              onClick={resetCanvas}
              disabled={submitting}
              className="flex-1 py-3 rounded-xl bg-white text-foreground-600 border border-background-300 text-sm font-bold cursor-pointer hover:bg-background-50 active:scale-[0.98] transition-all disabled:opacity-60"
            >
              清除重签
            </button>
            <button
              onClick={handleConfirm}
              disabled={!hasInk || submitting}
              className="flex-1 py-3 rounded-xl bg-primary-500 text-white text-sm font-bold cursor-pointer hover:bg-primary-600 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? '提交中...' : '确认签名'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
